import React, { useEffect } from "react";
import {
  DEFAULT_AMOUNT_SAMPLE,
  DEFAULT_LABORATORY,
  DEFAULT_VALID_PREPARATION,
} from "../../../utils/constants";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ProcedureType, UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
} from "@app/types/samples.types";
import useTabs from "@app/pages/samples/utils/useTab";
import { useSample } from "@app/pages/samples/utils/useSample";
import useAuth from "@app/context/AuthContext";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import { formatDateOrNow } from "@app/utils/formatDate";
import { OPTIONS_YES_NO } from "../iodo131/utils/constants";
import { setsAnalysisPhasesSR } from "@app/pages/samples/config/phases/AnalysisPhases";
import dayjs from "dayjs";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentMeasurement?: AnalysisCurrent,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  filteredProceduresPRE?: ProcedureType,
  filteredProceduresPREFine?: ProcedureType,
  filteredProceduresCON?: ProcedureType,
  filteredProceduresAA?: ProcedureType,
  filteredCalculationCAL?: ProcedureType,
  idMuestra?: string
) => {
  const currentMeasurementIndex: any =
    typeof currentMeasurement?.index === 'number' 
      ? currentPreparationIndex?.aa_measures?.[currentMeasurement.index]
      : undefined;

  const currentCountingIndex: any =
    typeof currentCounting?.index === 'number'
      ? currentMeasurementIndex?.countings?.[currentCounting.index]
      : undefined;
      
  const currentCalculationIndex =
    typeof currentCalculation?.index === 'number' && currentCountingIndex?.calculations
      ? currentCountingIndex.calculations[currentCalculation.index]
      : undefined;
  const currentSampling = sampling?.current;
  const defaultTypeUnit = sampling?.current?.unit_types;

  const calculateSamplingTime = (startDate, endDate) => {
    const start = dayjs(startDate).second(0).hour(0).minute(0);
    const end = dayjs(endDate).second(0).hour(0).minute(0);

    if (start.isSame(end, "day")) {
      return 1 * 24 * 60;
    }

    return end.diff(start, "day") * 24 * 60;
  };


  const calculatePrecipitationToSamplingEnd = (precipitationDate, endDate) => {
    const precipitation = dayjs(precipitationDate).second(0);
    const end = dayjs(endDate).second(0);
    return end.diff(precipitation, "minute");
  };

  const calculatePrecipitationToFirstCount = (
    precipitationDate,
    firstCountStartDate,
    firstCountDuration
  ) => {
    const precipitation = dayjs(precipitationDate).second(0);
    const firstCountStart = dayjs(firstCountStartDate).second(0);

    const midTimeFirstCount = firstCountStart.add(
      firstCountDuration / 2,
      "minute"
    );

    return midTimeFirstCount.diff(precipitation, "minute");
  };

  const calculatePrecipitationToSecondCount = (
    precipitationDate,
    secondCountStartDate,
    secondCountDuration
  ) => {
    const precipitation = dayjs(precipitationDate).second(0);
    const secondCountStart = dayjs(secondCountStartDate).second(0);
    const midTimeSecondCount = secondCountStart.add(
      secondCountDuration / 2,
      "minute"
    );
    return midTimeSecondCount.diff(precipitation, "minute");
  };

  const currentSample = useSample.getState().get(Number(idMuestra));

  return {
    analysis_analyst: currentPreparationIndex?.analyst || user,
    preparation_procedure:
      currentPreparationIndex?.preparation_procedure ||
      filteredProceduresPREFine ||
      undefined,
    preparation_procedure_gross:
      currentPreparationIndex?.preparation_procedure_gross ||
      filteredProceduresPRE ||
      undefined,
    start_date: formatDateOrNow(currentPreparationIndex?.start_date),
    start_date_precipitation: formatDateOrNow(
      currentPreparationIndex?.start_date_precipitation
    ),
    analysis_amount_sample: currentPreparationIndex?.amount_sample || 0,
    analysis_equipment: currentPreparationIndex?.equipment || [],
    scintillator_lot_number:
      currentPreparationIndex?.scintillator_lot_number || undefined,
    analysis_unit_types: currentPreparationIndex?.unit_types || defaultTypeUnit,
    valid_preparation: DEFAULT_VALID_PREPARATION,
    planchette: currentPreparationIndex?.planchette || null,
    planchette_with_precipitate:
      currentPreparationIndex?.planchette_with_precipitate || null,
    preparation_performance: currentPreparationIndex?.performance || undefined,
    yellow_precipitate_silver_nitrate:
      currentPreparationIndex?.yellow_precipitate_silver_nitrate !== undefined
        ? OPTIONS_YES_NO.find(
            (option) =>
              option.value ===
              Number(currentPreparationIndex?.yellow_precipitate_silver_nitrate)
          )
        : undefined,
    carrier_concentration:
      currentPreparationIndex?.carrier_concentration || null,
    amount_ashes: currentPreparationIndex?.amount_ashes || undefined,
    amount_equivalent: currentPreparationIndex?.amount_equivalent || undefined,

    //mesurement
    measurement_analyst: currentMeasurementIndex?.analyst || user,
    measurement_processing_procedure:
      currentMeasurementIndex?.procedure || filteredProceduresAA || undefined,
    measurement_equipment: currentMeasurementIndex?.equipment || [],
    measurement_date_time: formatDateOrNow(
      currentMeasurementIndex?.start_date_time
    ),
    sr_aa_mg_l: currentMeasurementIndex?.sr_aa_mg_l || null,
    sr_aa_mg: currentMeasurementIndex?.sr_aa_mg,
    measurement_performance: currentMeasurementIndex?.performance || undefined,

    sr_co3_aa: currentMeasurementIndex?.sr_co3_aa || undefined,
    valid_measure: currentMeasurementIndex?.valid_measure || null,
    result_to_calculation: currentMeasurementIndex?.result_to_calculation || 0,
    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure:
      currentCountingIndex?.counting_procedure || filteredProceduresCON,
    counting_equipment: currentCountingIndex?.equipment || undefined,
    counting_detector: currentCountingIndex?.detectors || undefined,
    counting_date: formatDateOrNow(currentCountingIndex?.counting_date),
    first_counting_date: formatDateOrNow(
      currentCountingIndex?.first_counting_date
    ),
    first_counting_time: currentCountingIndex?.first_counting_time || undefined,
    first_counting_cpm: currentCountingIndex?.first_counting_cpm || undefined,
    second_counting_date: formatDateOrNow(
      currentCountingIndex?.second_counting_date
    ),
    second_counting_time:
      currentCountingIndex?.second_counting_time || undefined,
    second_counting_cpm: currentCountingIndex?.second_counting_cpm || undefined,
    counting_valid: "NA",

    //calculation
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_equipment: currentCalculationIndex?.equipment
      ? currentCalculationIndex?.equipment
      : currentCountingIndex?.equipment,
    calculation_analyst: currentCalculationIndex?.analyst || user,
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_amount_sample: currentPreparationIndex?.amount_equivalent || undefined,
    calculation_units: currentPreparationIndex?.unit_types || undefined,
    calculation_tsie_sample: currentCountingIndex?.sqp_tsie_sample || undefined,
    calculation_shared: currentSample?.shared ? "Si" : "No",
    calculation_counting_date: currentCountingIndex?.counting_date || undefined,
    sample_gross_cpm: currentCountingIndex?.sample_cpm || undefined,
    gross_target_cpm: currentCountingIndex?.gross_white_cpm || undefined,
    total_time_counting:
      currentCalculationIndex?.total_time_counting || undefined,
    calculation: currentCalculationIndex?.calculation || "",
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || filteredCalculationCAL,
    sampledAirVolumeSilicagel:
      currentPreparationIndex?.sampledAirVolumeSilicagel || "",
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
    calculation_detector: currentCountingIndex?.detectors || [],
    calculation_start_date_precipitation: formatDateOrNow(
      currentPreparationIndex?.start_date_precipitation
    ),
    calculation_tsie_white: currentPreparationIndex?.mg_torta,
    calculation_performance:
      currentCalculationIndex?.calculation_performance ||
      currentMeasurementIndex?.result_to_calculation ||
      currentPreparationIndex?.performance,
    //calculation_dry_wet_weight: currentPreparationIndex?.mg_torta,
    calculation_first_counting_date: formatDateOrNow(
      currentCountingIndex?.first_counting_date
    ),
    calculation_first_counting_time: currentCountingIndex?.first_counting_time,
    calculation_first_counting_cpm: currentCountingIndex?.first_counting_cpm,
    calculation_second_counting_date: formatDateOrNow(
      currentCountingIndex?.second_counting_date
    ),
    calculation_second_counting_time:
      currentCalculationIndex?.calculation_second_counting_cpm ||
      currentCountingIndex?.second_counting_time,
    calculation_second_counting_cpm:
      currentCalculationIndex?.calculation_second_counting_cpm ||
      currentCountingIndex?.second_counting_cpm,
    calculation_sampling_time_min:
      (currentCalculationIndex?.calculation_sampling_time_min && 
       currentCalculationIndex?.calculation_sampling_time_min > 0) 
        ? currentCalculationIndex?.calculation_sampling_time_min
        : calculateSamplingTime(
            currentSampling?.date_start_sampling,
            currentSampling?.date_end_sampling
          ),
    calculation_precipitation_final_time:
      currentCalculationIndex?.calculation_precipitation_final_time ||
      calculatePrecipitationToSamplingEnd(
        currentSampling?.date_end_sampling,
        currentPreparationIndex?.start_date_precipitation
      ),
    calculation_precipitation_first_counting:
      currentCalculationIndex?.calculation_precipitation_first_counting ||
      calculatePrecipitationToFirstCount(
        currentPreparationIndex?.start_date_precipitation,
        currentCountingIndex?.first_counting_date,
        currentCountingIndex?.first_counting_time
      ),
    calculation_fondo: currentCalculationIndex?.fondos || undefined,
    calculation_precipitation_second_counting:
      currentCalculationIndex?.calculation_precipitation_second_counting ||
      calculatePrecipitationToSecondCount(
        currentPreparationIndex?.start_date_precipitation,
        currentCountingIndex?.second_counting_date,
        currentCountingIndex?.second_counting_time
      ),
  };
};

const SRForm = () => {
  const { tabValueAnalysis } = useTabs();

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { user } = useAuth();
  const filteredProceduresPRE = useFilteredProcedures("PRE");
  const filteredProceduresPREFine = useFilteredProcedures("PRE", "SFINA");
  const filteredProceduresCON = useFilteredProcedures("CON");
  const filteredProceduresAA = useFilteredProcedures("AA");
  const filteredCalculationCAL = useFilteredProcedures("CAL");

  const {
    currentPreparation,
    currentMeasurement,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const selectedAnalysis = form.getValues("typeAnalysis");

  const filteredPreparationsList = preparationsList.filter((preparation) =>
    preparation?.analysis_type?.some(
      (analysis) => analysis.id === selectedAnalysis.id
    )
  );

  const currentPreparationObject =
    filteredPreparationsList[currentPreparation.index as number];

  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentMeasurement,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          filteredProceduresPRE?.[0],
          filteredProceduresPREFine?.[0],
          filteredProceduresCON?.[0],
          filteredProceduresAA?.[0],
          filteredCalculationCAL?.[0],
          idMuestra
        ),
      };

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]); // eslint-disable-line react-hooks/exhaustive-deps

  const CurrentComponent = setsAnalysisPhasesSR[tabValueAnalysis]?.component;
  return CurrentComponent ? <CurrentComponent /> : null;
};

export default SRForm;
